import { Component, OnInit } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import _ from 'lodash';
import { SubscribedComponent } from '../../subscribed.component';
import {
  CurationCandidate,
  SkillCurationService,
} from '../../shared/skill-curation.service';
import { SearchService } from '../../shared/search.service';
import { SearchResult } from '../../interfaces/searchResult';
import { Skill } from '../../interfaces/skill';

@Component({
  selector: 'bst-skill-curation',
  templateUrl: './skill-curation.component.html',
  styleUrls: ['./skill-curation.component.scss'],
  standalone: false,
})
export class SkillCurationComponent extends SubscribedComponent implements OnInit {

  candidates: CurationCandidate[] = [];
  meta = { current_page: 1, last_page: 1, per_page: 15, total: 0 };

  /** Ids selected for a bulk action. */
  selectedIds = new Set<number>();
  /** Rows with an action in flight — buttons disabled to prevent double-submit. */
  busyIds = new Set<number>();

  // Inline merge typeahead state (one row open at a time).
  mergeRowId: number | null = null;
  mergeQuery = '';
  mergeResults: Skill[] = [];
  mergeSearching = false;

  private readonly runMergeSearch: () => void;

  constructor(
    private curation: SkillCurationService,
    private search: SearchService,
    private toastr: NbToastrService,
  ) {
    super();
    this.runMergeSearch = _.debounce(() => this.doMergeSearch(), 250);
  }

  ngOnInit(): void {
    super.ngOnInit();
    void this.load(1);
  }

  async load(page: number): Promise<void> {
    this.loading.next(true);
    this.closeMerge();
    try {
      const res = await this.curation.list(page);
      this.candidates = res?.data ?? [];
      if (res?.meta) { this.meta = res.meta; }
      // A mutation (single or bulk) may have emptied this page; step back so the
      // curator never lands on a blank page that still has rows behind it. Guard
      // on `res` present: an empty body must NOT be read as an emptied page,
      // else a transient blip walks the curator backward a page at a time.
      if (res && this.candidates.length === 0 && this.meta.total > 0 && page > 1) {
        // Await the fallback so `finally` doesn't drop the spinner (and flash the
        // empty state) while the step-back page is still loading.
        await this.load(Math.min(page - 1, this.meta.last_page || 1));
        return;
      }
      // Drop selections that are no longer on the visible page.
      const visible = new Set(this.candidates.map(c => c.id));
      this.selectedIds = new Set([...this.selectedIds].filter(id => visible.has(id)));
    } finally {
      this.loading.next(false);
    }
  }

  // === Selection ===

  toggleSelect(id: number): void {
    if (this.selectedIds.has(id)) { this.selectedIds.delete(id); }
    else { this.selectedIds.add(id); }
  }

  get allSelected(): boolean {
    return this.candidates.length > 0 && this.candidates.every(c => this.selectedIds.has(c.id));
  }

  toggleSelectAll(): void {
    if (this.allSelected) {
      this.selectedIds.clear();
    } else {
      this.candidates.forEach(c => this.selectedIds.add(c.id));
    }
  }

  // === Row actions ===

  async approve(row: CurationCandidate): Promise<void> {
    await this.runRowAction(row, () => this.curation.approve(row.id));
  }

  async reject(row: CurationCandidate): Promise<void> {
    await this.runRowAction(row, () => this.curation.reject(row.id));
  }

  async reclassify(row: CurationCandidate): Promise<void> {
    await this.runRowAction(row, () => this.curation.reclassifyDevelopment(row.id));
  }

  private async runRowAction(row: CurationCandidate, action: () => Promise<any>): Promise<void> {
    if (this.busyIds.has(row.id)) { return; }
    this.busyIds.add(row.id);
    try {
      await action();
      this.selectedIds.delete(row.id);
      await this.reloadAfterMutation();
    } catch {
      // Error already surfaced by APIService's toast; swallow so the template
      // click handler doesn't leave an unhandled rejection.
    } finally {
      this.busyIds.delete(row.id);
    }
  }

  // === Merge typeahead ===

  openMerge(row: CurationCandidate): void {
    this.mergeRowId = row.id;
    this.mergeQuery = row.nearest_match_name ?? row.name;
    this.mergeResults = [];
    this.runMergeSearch();
  }

  closeMerge(): void {
    this.mergeRowId = null;
    this.mergeQuery = '';
    this.mergeResults = [];
    this.mergeSearching = false;
  }

  onMergeQueryChange(value: string): void {
    this.mergeQuery = value;
    this.runMergeSearch();
  }

  private async doMergeSearch(): Promise<void> {
    // Snapshot the request identity; debounce cuts calls but two requests can
    // still be in flight and resolve out of order.
    const rowId = this.mergeRowId;
    const q = this.mergeQuery.trim();
    if (rowId === null || q.length < 2) { this.mergeResults = []; this.mergeSearching = false; return; }
    this.mergeSearching = true;
    try {
      const res: SearchResult<Skill> = await this.search.search('skill', { search: q });
      // Discard a stale response: the user may have switched rows, retyped, or
      // closed the panel while this request was in flight.
      if (!this.isCurrentMergeSearch(rowId, q)) { return; }
      // Never offer the row being merged as its own target.
      this.mergeResults = (res?.results ?? []).filter(s => s.id !== rowId);
    } catch {
      if (this.isCurrentMergeSearch(rowId, q)) { this.mergeResults = []; }
    } finally {
      // Only the request that still owns the panel clears the spinner; a newer
      // in-flight request keeps it on.
      if (this.isCurrentMergeSearch(rowId, q)) { this.mergeSearching = false; }
    }
  }

  private isCurrentMergeSearch(rowId: number, query: string): boolean {
    return this.mergeRowId === rowId && this.mergeQuery.trim() === query;
  }

  async confirmMerge(row: CurationCandidate, target: Skill): Promise<void> {
    if (this.busyIds.has(row.id)) { return; }
    this.busyIds.add(row.id);
    try {
      await this.curation.merge(row.id, target.id);
      this.selectedIds.delete(row.id);
      this.closeMerge();
      await this.reloadAfterMutation();
    } catch {
      // APIService already toasted; swallow the rejection.
    } finally {
      this.busyIds.delete(row.id);
    }
  }

  // === Bulk ===

  get bulkCount(): number { return this.selectedIds.size; }

  async bulkApprove(): Promise<void> { await this.runBulk('approve'); }
  async bulkReject(): Promise<void> { await this.runBulk('reject'); }

  private async runBulk(action: 'approve' | 'reject'): Promise<void> {
    const ids = [...this.selectedIds];
    if (ids.length === 0) { return; }
    this.loading.next(true);
    try {
      const res = await this.curation.bulk(action, ids);
      const processed = res?.processed ?? 0;
      const skipped = res?.skipped?.length ?? 0;
      const msg = skipped > 0
        ? `${processed} ${action}d, ${skipped} skipped`
        : `${processed} ${action}d`;
      this.toastr.success(msg, 'Bulk done');
      this.selectedIds.clear();
      await this.reloadAfterMutation();
    } catch {
      // APIService already toasted; swallow the rejection.
    } finally {
      this.loading.next(false);
    }
  }

  // === Helpers ===

  /** Reload the current page; load() steps back if the page emptied out. */
  private async reloadAfterMutation(): Promise<void> {
    await this.load(this.meta.current_page);
  }

  scorePercent(score: number | string | null): string | null {
    if (score === null || score === '') { return null; }
    const n = typeof score === 'string' ? parseFloat(score) : score;
    if (isNaN(n)) { return null; }
    return `${Math.round(n * 100)}%`;
  }
}
